/**
 * Fancy & Bookmaker commission — Lab `fancyAndBookmakerCommission` /
 * `account/getTotalCommissionOnDetailPage`.
 *
 * Live: CM-API `POST /v1/account/getTotalCommissionOnDetailPage` (shared DB).
 * Any `CmApiHttpError` → `unavailable: true` (UI StateBlock); never fake success zeros.
 */
import { CmApiHttpError, cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'

export type FancyBookmakerCommissionQuery = {
  /** Seat whose stamped commission columns are summed (Lab `memberId`). */
  memberId: string
  /** Optional downline filter (Lab `childMemberId`); omit / `All` = every child. */
  childMemberId?: string
  from: string
  to: string
  /** Bumped on every Search so same From/To still hits the network. */
  searchNonce?: number
}

export type FancyBookmakerCommissionResult = {
  unavailable: boolean
  reason?: string
  asOf: string
  fancy: {
    upline: number
    downline: number
    profit: number
  }
  bookmaker: {
    upline: number
    downline: number
    profit: number
  }
}

/** CM-API Standard API Response for commission detail. */
export type GetTotalCommissionOnDetailPageResponse = {
  status: number
  message: string
  data?: {
    fancyUplineCommission?: number
    fancyDownlineCommission?: number
    bookmakerUplineCommission?: number
    bookmakerDownlineCommission?: number
    fancyCommAmount?: number
    bookmakerCommAmount?: number
  }
  error?: string
}

export function unavailableFancyBookmakerCommission(
  reason = 'Fancy & Bookmaker commission is not available.',
): FancyBookmakerCommissionResult {
  return {
    unavailable: true,
    reason,
    asOf: new Date().toISOString(),
    fancy: { upline: 0, downline: 0, profit: 0 },
    bookmaker: { upline: 0, downline: 0, profit: 0 },
  }
}

export function mapLabCommissionDetail(
  data: GetTotalCommissionOnDetailPageResponse['data'] | undefined,
): FancyBookmakerCommissionResult {
  const d = data ?? {}
  return {
    unavailable: false,
    asOf: new Date().toISOString(),
    fancy: {
      upline: Number(d.fancyUplineCommission ?? 0) || 0,
      downline: Number(d.fancyDownlineCommission ?? 0) || 0,
      profit: Number(d.fancyCommAmount ?? 0) || 0,
    },
    bookmaker: {
      upline: Number(d.bookmakerUplineCommission ?? 0) || 0,
      downline: Number(d.bookmakerDownlineCommission ?? 0) || 0,
      profit: Number(d.bookmakerCommAmount ?? 0) || 0,
    },
  }
}

/**
 * Lab `member-commission` tile: when upline > 0, template prefixes `-`
 * before indianCurrency (debit-style). Zero / already-negative stay as-is.
 */
export function labMemberCommissionDisplay(upline: number): number {
  return upline > 0 ? -upline : upline
}

/** Lab `Account/getTotalCommissionOnDetailPage` → CM-API. */
export async function getTotalCommissionOnDetailPage(
  query: FancyBookmakerCommissionQuery,
): Promise<GetTotalCommissionOnDetailPageResponse> {
  const { searchNonce: _nonce, ...request } = query
  const out: Record<string, unknown> = {
    memberId: request.memberId,
    fromDate: request.from,
    toDate: request.to,
  }
  const child = request.childMemberId?.trim()
  if (child) out.childMemberId = child
  return cmApi.getTotalCommissionOnDetailPage(authHeaders(), out)
}

export async function loadFancyBookmakerCommission(
  query: FancyBookmakerCommissionQuery,
): Promise<FancyBookmakerCommissionResult> {
  if (!query.memberId || !query.from || !query.to) {
    return unavailableFancyBookmakerCommission('Choose a member and a date range.')
  }
  try {
    const res = await getTotalCommissionOnDetailPage(query)
    if (res.status !== 200) {
      return unavailableFancyBookmakerCommission(
        res.error?.trim() || res.message || 'Commission detail was refused by CM-API.',
      )
    }
    return mapLabCommissionDetail(res.data)
  } catch (err) {
    if (err instanceof CmApiHttpError) {
      return unavailableFancyBookmakerCommission(
        err.message?.trim() || `Commission detail failed (${err.status}).`,
      )
    }
    return unavailableFancyBookmakerCommission(
      err instanceof Error ? err.message : 'Fancy & Bookmaker commission request failed.',
    )
  }
}
